import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "../api";
import PageLayout from "../components/PageLayout";
import { useToast } from "../context/ToastContext";
import { io } from "socket.io-client";
import DomainScannerModal from "../components/DomainScannerModal";
import ScoreBadge from "../components/ScoreBadge";
import { formatDateTime, formatDateOnly } from "../utils/formatDate";

const scansPageStyle = `
  :root {
    --s360-ink: #0f172a;
    --s360-muted: #64748b;
    --s360-line: #e2e8f0;
    --s360-surface: #f8fafc;
    --s360-accent: #0b6bcb;
    --s360-accent-soft: #e8f2fc;
    --s360-ok: #059669;
  }

  @keyframes pulse-green {
    0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(5, 150, 105, 0.55); }
    70% { transform: scale(1); box-shadow: 0 0 0 8px rgba(5, 150, 105, 0); }
    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(5, 150, 105, 0); }
  }
  @keyframes s360-fade-up {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .dot-pulse {
    width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 8px;
  }
  .dot-green { background: var(--s360-ok); animation: pulse-green 2s infinite; }
  .dot-yellow { background: #d97706; }
  .dot-red { background: #dc2626; }

  .scans-shell {
    font-family: "Outfit", system-ui, sans-serif;
    background: linear-gradient(180deg, #f1f5f9 0%, #ffffff 42%);
    border: 1px solid var(--s360-line);
    border-radius: 16px;
    overflow: visible;
  }
  .scans-table-wrap {
    overflow-x: auto;
    overflow-y: visible;
  }
  .scans-toolbar {
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid var(--s360-line);
    background: rgba(255,255,255,0.86);
    backdrop-filter: blur(8px);
  }
  .scans-title {
    color: var(--s360-ink);
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0;
  }
  .scans-count {
    background: var(--s360-accent-soft);
    color: var(--s360-accent);
    border-radius: 999px;
    padding: 0.2rem 0.65rem;
    font-size: 0.8rem;
    font-weight: 700;
  }
  .scans-table thead th {
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--s360-muted);
    font-weight: 600;
    border-bottom-width: 1px;
    background: var(--s360-surface);
    white-space: nowrap;
  }
  .scans-table tbody td {
    vertical-align: middle;
    border-color: var(--s360-line);
  }
  .scans-domain {
    font-weight: 650;
    color: var(--s360-ink);
    letter-spacing: -0.01em;
  }
  .scans-actions {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    justify-content: flex-end;
  }
  .scans-actions .btn {
    border-radius: 10px;
    padding: 0.35rem 0.65rem;
    font-size: 0.8rem;
    font-weight: 600;
  }
  .tools-menu-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1080;
    background: transparent;
  }
  .tools-menu {
    position: fixed;
    z-index: 1081;
    box-sizing: border-box;
    background: #fff;
    border: 1px solid var(--s360-line);
    border-radius: 12px;
    padding: 0.5rem;
    animation: s360-fade-up 0.16s ease-out;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }
  .tools-menu::-webkit-scrollbar {
    width: 6px;
  }
  .tools-menu::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }
  .tools-menu-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.15rem 0.35rem;
  }
  .tools-menu-grid .tools-section {
    grid-column: 1 / -1;
  }
  .tools-menu-grid .tools-sep {
    grid-column: 1 / -1;
  }
  .tools-menu-grid .tools-span {
    grid-column: 1 / -1;
  }
  @media (max-width: 420px) {
    .tools-menu-grid {
      grid-template-columns: 1fr;
    }
  }
  .tools-menu .tools-section {
    font-size: 0.68rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--s360-muted);
    font-weight: 700;
    padding: 0.4rem 0.55rem 0.15rem;
  }
  .tools-menu button.tools-item {
    display: flex;
    align-items: center;
    width: 100%;
    border: none;
    background: transparent;
    border-radius: 8px;
    font-size: 0.82rem;
    padding: 0.48rem 0.55rem;
    color: var(--s360-ink);
    text-align: left;
    font-family: inherit;
    line-height: 1.25;
    cursor: pointer;
  }
  .tools-menu button.tools-item i {
    font-size: 1rem;
    width: 1.1rem;
    flex-shrink: 0;
  }
  .tools-menu button.tools-item:hover,
  .tools-menu button.tools-item:focus-visible {
    background: var(--s360-accent-soft);
    color: var(--s360-accent);
    outline: none;
  }
  .tools-menu button.tools-item.is-danger {
    color: #dc2626;
  }
  .tools-menu button.tools-item.is-danger:hover,
  .tools-menu button.tools-item.is-danger:focus-visible {
    background: #fef2f2;
    color: #b91c1c;
  }
  .tools-menu .tools-sep {
    height: 1px;
    background: var(--s360-line);
    margin: 0.3rem 0.25rem;
  }

  .connect-modal {
    font-family: "Outfit", system-ui, sans-serif;
    border: 1px solid var(--s360-line);
    border-radius: 20px;
    overflow: hidden;
    animation: s360-fade-up 0.28s ease-out;
    box-shadow: 0 24px 48px rgba(15, 23, 42, 0.14), 0 8px 16px rgba(15, 23, 42, 0.06);
  }
  .connect-modal .modal-header {
    border-bottom: none;
    background: linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%);
    padding: 1.35rem 1.5rem 1rem;
  }
  .connect-modal .modal-title {
    color: var(--s360-ink);
    font-weight: 700;
    letter-spacing: -0.02em;
    font-size: 1.35rem;
  }
  .connect-modal .modal-body {
    padding: 0 1.5rem 1.25rem;
    background: #fff;
  }
  .connect-modal .modal-footer {
    border-top: 1px solid var(--s360-line);
    background: #fafbfc;
    padding: 1rem 1.5rem 1.25rem;
    gap: 0.65rem;
  }
  .connect-domain-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.5rem;
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    background: #fff;
    border: 1px solid var(--s360-line);
    color: var(--s360-ink);
    font-size: 0.82rem;
    font-weight: 600;
  }
  .connect-wizard {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.35rem;
    margin: 0 0 1.5rem;
    padding: 0 0.15rem;
  }
  .connect-wizard-step {
    flex: 1;
    text-align: center;
    position: relative;
  }
  .connect-wizard-step:not(:last-child)::after {
    content: "";
    position: absolute;
    top: 14px;
    left: calc(50% + 18px);
    width: calc(100% - 36px);
    height: 2px;
    background: var(--s360-line);
    z-index: 0;
  }
  .connect-wizard-step.is-done:not(:last-child)::after {
    background: var(--s360-accent);
  }
  .connect-wizard-dot {
    width: 28px;
    height: 28px;
    margin: 0 auto 0.45rem;
    border-radius: 50%;
    border: 2px solid var(--s360-line);
    background: #fff;
    color: var(--s360-muted);
    font-size: 0.72rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
    transition: all 0.2s ease;
  }
  .connect-wizard-step.is-active .connect-wizard-dot {
    border-color: var(--s360-accent);
    background: var(--s360-accent);
    color: #fff;
    box-shadow: 0 0 0 4px rgba(11, 107, 203, 0.15);
  }
  .connect-wizard-step.is-done .connect-wizard-dot {
    border-color: var(--s360-accent);
    background: var(--s360-accent-soft);
    color: var(--s360-accent);
  }
  .connect-wizard-label {
    font-size: 0.68rem;
    font-weight: 600;
    color: var(--s360-muted);
    letter-spacing: 0.02em;
  }
  .connect-wizard-step.is-active .connect-wizard-label {
    color: var(--s360-accent);
  }
  .connect-wizard-step.is-done .connect-wizard-label {
    color: var(--s360-ink);
  }
  .connect-steps {
    display: none;
  }
  .connect-kicker {
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--s360-accent);
    font-weight: 700;
    margin-bottom: 0.35rem;
  }
  .connect-lead {
    color: var(--s360-muted);
    font-size: 0.95rem;
    margin-bottom: 1.25rem;
    line-height: 1.5;
  }
  .connect-option {
    width: 100%;
    text-align: left;
    border: 2px solid var(--s360-line);
    border-radius: 14px;
    background: #fff;
    padding: 0.9rem 1rem;
    margin-bottom: 0.65rem;
    transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
  }
  .connect-option:hover:not(:disabled):not(.is-disabled) {
    border-color: #93c5fd;
    box-shadow: 0 4px 14px rgba(11, 107, 203, 0.08);
  }
  .connect-option.is-selected {
    border-color: var(--s360-accent);
    background: linear-gradient(135deg, #f0f7ff 0%, #fff 100%);
    box-shadow: 0 4px 16px rgba(11, 107, 203, 0.12);
  }
  .connect-option.is-disabled {
    opacity: 0.72;
    cursor: not-allowed;
    background: #f8fafc;
  }
  .connect-option-inline-icon {
    font-size: 1.05rem;
    color: var(--s360-muted);
    margin-right: 0.35rem;
    vertical-align: -2px;
  }
  .connect-option.is-selected .connect-option-inline-icon {
    color: var(--s360-accent);
  }
  .connect-option.is-disabled .connect-option-inline-icon {
    color: #94a3b8;
  }
  .connect-option-title {
    margin: 0;
    font-size: 0.98rem;
    font-weight: 700;
    color: var(--s360-ink);
  }
  .connect-option-desc {
    margin: 0.2rem 0 0;
    font-size: 0.84rem;
    color: var(--s360-muted);
    line-height: 1.45;
  }
  .connect-pill {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 0.15rem 0.55rem;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin-left: 0.5rem;
  }
  .connect-pill.rec {
    background: #d1fae5;
    color: #047857;
  }
  .connect-pill.pro {
    background: #e2e8f0;
    color: #334155;
  }
  .connect-radio {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid #cbd5e1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
    background: #fff;
    transition: border-color 0.15s ease, background 0.15s ease;
  }
  .connect-option.is-selected .connect-radio {
    border-color: var(--s360-accent);
    background: var(--s360-accent);
    color: #fff;
  }
  .connect-option.is-disabled .connect-radio {
    border-color: #e2e8f0;
    background: #f8fafc;
  }
  .connect-cta {
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #0b6bcb 0%, #0959a8 100%);
    color: #fff;
    font-weight: 700;
    padding: 0.85rem 1.25rem;
    transition: opacity 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
    box-shadow: 0 4px 12px rgba(11, 107, 203, 0.25);
  }
  .connect-cta:hover:not(:disabled) {
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(11, 107, 203, 0.3);
  }
  .connect-cta:disabled {
    opacity: 0.65;
  }
  .connect-back {
    border: 1px solid var(--s360-line);
    border-radius: 12px;
    background: #fff;
    color: var(--s360-muted);
    font-weight: 600;
    padding: 0.85rem 1.25rem;
    transition: background 0.15s ease, color 0.15s ease;
  }
  .connect-back:hover {
    background: #f8fafc;
    color: var(--s360-ink);
  }
  .connect-secondary {
    width: 100%;
    border: none;
    background: transparent;
    color: var(--s360-muted);
    font-weight: 600;
    padding: 0.5rem;
    font-size: 0.88rem;
  }
  .connect-secondary:hover {
    color: var(--s360-ink);
  }
  .connect-record {
    border: 1px solid #bfdbfe;
    border-radius: 16px;
    background: linear-gradient(180deg, #f8fbff 0%, #fff 100%);
    padding: 1.15rem;
  }
  .connect-record label {
    font-size: 0.68rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--s360-muted);
    font-weight: 700;
    margin-bottom: 0.25rem;
  }
`;

const Scans = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  let user = {};
  try {
    user = JSON.parse(sessionStorage.getItem("user") || "{}");
  } catch (e) {
    console.error("Failed to parse user data", e);
  }
  const user_id = user.id;

  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [domainName, setDomainName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [statusFilter, setStatusFilter] = useState("All");
  const [scanningDomainId, setScanningDomainId] = useState(null);
  const [modalStep, setModalStep] = useState(1);
  const [connectionType, setConnectionType] = useState("manual");
  const [verifying, setVerifying] = useState(false);
  const [activeDomainForVerify, setActiveDomainForVerify] = useState(null);
  const [scannerModalDomain, setScannerModalDomain] = useState(null);
  const [toolsMenu, setToolsMenu] = useState(null);
  const toolsMenuRef = useRef(null);
  const toolsAnchorRef = useRef(null);
  const [discoveredCount, setDiscoveredCount] = useState(0);
  const [showDiscoveryModal, setShowDiscoveryModal] = useState(false);
  const [discoveredAssets, setDiscoveredAssets] = useState([]);
  const [showFindingsModal, setShowFindingsModal] = useState(false);
  const [openFindings, setOpenFindings] = useState([]);
  const [findingsLoading, setFindingsLoading] = useState(false);

  const closeToolsMenu = useCallback(() => {
    toolsAnchorRef.current = null;
    setToolsMenu(null);
  }, []);

  const computeToolsPosition = useCallback((anchorEl, menuEl) => {
    const rect = anchorEl.getBoundingClientRect();
    const padding = 8;
    const isNarrow = window.innerWidth < 420;
    const menuWidth = Math.min(isNarrow ? window.innerWidth - padding * 2 : 320, window.innerWidth - padding * 2);
    const menuHeight = menuEl ? menuEl.offsetHeight : (isNarrow ? 460 : 280);
    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;

    let left = rect.right - menuWidth;
    left = Math.min(Math.max(padding, left), window.innerWidth - menuWidth - padding);

    let top;
    let maxHeight;
    const openUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;
    if (openUp) {
      maxHeight = Math.max(180, spaceAbove - 6);
      top = Math.max(padding, rect.top - Math.min(menuHeight, maxHeight) - 6);
    } else {
      maxHeight = Math.max(180, spaceBelow - 6);
      top = rect.bottom + 6;
    }

    return { top, left, width: menuWidth, maxHeight, placement: openUp ? "top" : "bottom" };
  }, []);

  const openToolsMenu = useCallback((event, domain) => {
    event.preventDefault();
    event.stopPropagation();
    const anchor = event.currentTarget;
    toolsAnchorRef.current = anchor;
    const approx = computeToolsPosition(anchor, null);
    setToolsMenu({ domain, ...approx });
  }, [computeToolsPosition]);

  useLayoutEffect(() => {
    if (!toolsMenu?.domain?.id || !toolsAnchorRef.current) return;
    const menuEl = toolsMenuRef.current;
    if (!menuEl) return;
    const next = computeToolsPosition(toolsAnchorRef.current, menuEl);
    setToolsMenu((prev) => {
      if (!prev) return prev;
      if (
        prev.top === next.top &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.maxHeight === next.maxHeight
      ) {
        return prev;
      }
      return { ...prev, ...next };
    });
    // Re-measure once after open / domain change only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolsMenu?.domain?.id, computeToolsPosition]);

  useEffect(() => {
    if (!toolsMenu?.domain?.id) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") closeToolsMenu();
    };

    const onViewportChange = () => {
      if (!toolsAnchorRef.current) {
        closeToolsMenu();
        return;
      }
      const rect = toolsAnchorRef.current.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth) {
        closeToolsMenu();
        return;
      }
      const next = computeToolsPosition(toolsAnchorRef.current, toolsMenuRef.current);
      setToolsMenu((prev) => (prev ? { ...prev, ...next } : prev));
    };

    const onScroll = (e) => {
      const menu = toolsMenuRef.current;
      if (menu && (e.target === menu || menu.contains(e.target))) return;
      closeToolsMenu();
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [toolsMenu?.domain?.id, closeToolsMenu, computeToolsPosition]);

  const runToolsAction = useCallback((action) => {
    closeToolsMenu();
    action();
  }, [closeToolsMenu]);

  const fetchDomains = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/domains`);
      if (Array.isArray(res.data)) {
        setDomains(res.data);
      } else {
        console.error("API Error: Expected array of domains, got:", res.data);
        setDomains([]);
      }
    } catch (err) {
      console.error("Error fetching domains:", err);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    fetchDomains();
    const interval = setInterval(fetchDomains, 60000);

    const socket = io(window.location.origin.replace(":3000", ":5100")); // Fallback to backend port if on 3000
    socket.emit("join", user_id);

    socket.on("verification_lost", ({ domain, message }) => {
      toastError(message);
      fetchDomains(); // Refresh list to show red dot
    });

    return () => {
      clearInterval(interval);
      socket.disconnect();
    };
  }, [user_id, toastError, fetchDomains]);

  useEffect(() => {
    api.get('/api/discovered-assets/count')
      .then((res) => {
        if (res.data.success) setDiscoveredCount(res.data.data.count);
      })
      .catch(() => {});
  }, [domains]);

  const loadDiscoveredAssets = async () => {
    try {
      const res = await api.get('/api/discovered-assets', { params: { status: 'new', limit: 50 } });
      if (res.data.success) setDiscoveredAssets(res.data.data);
      setShowDiscoveryModal(true);
    } catch {
      toastError('Failed to load discovered assets');
    }
  };

  const loadOpenFindings = async () => {
    setFindingsLoading(true);
    try {
      const res = await api.get('/api/findings', { params: { status: 'open', limit: 50 } });
      if (res.data.success) {
        setOpenFindings(res.data.data);
        setShowFindingsModal(true);
      }
    } catch {
      toastError('Failed to load open issues');
    } finally {
      setFindingsLoading(false);
    }
  };

  const handleClaimAsset = async (id) => {
    try {
      const res = await api.patch(`/api/discovered-assets/${id}/claim`);
      toastSuccess(res.data.message || 'Domain added — complete TXT verification');
      setDiscoveredAssets((prev) => prev.filter((a) => a.id !== id));
      setDiscoveredCount((c) => Math.max(0, c - 1));
      fetchDomains();
    } catch (err) {
      toastError(err.response?.data?.error || 'Claim failed');
    }
  };

  const handleDismissAsset = async (id) => {
    try {
      await api.patch(`/api/discovered-assets/${id}/dismiss`);
      setDiscoveredAssets((prev) => prev.filter((a) => a.id !== id));
      setDiscoveredCount((c) => Math.max(0, c - 1));
    } catch {
      toastError('Dismiss failed');
    }
  };

  const handleAcknowledgeFinding = async (id) => {
    try {
      await api.patch(`/api/findings/${id}`, { status: 'acknowledged', note: 'Acknowledged from Scans page' });
      setOpenFindings((prev) => prev.filter((f) => f.id !== id));
      toastSuccess('Issue acknowledged');
    } catch {
      toastError('Failed to acknowledge');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!domainName.trim()) return toastError("Domain name required");

    try {
      if (editingDomain) {
        await api.put(`/api/domains/${editingDomain.id}`, {
          domain_name: domainName,
        });
        toastSuccess("Domain updated successfully");
      } else {
        const res = await api.post(`/api/domains/add`, {
          user_id,
          domain_name: domainName,
        });
        setActiveDomainForVerify(res.data.domain);
        setModalStep(2);
        toastSuccess("Domain added. Please complete verification.");
        fetchDomains();
        return; // Don't close modal yet
      }
      setShowModal(false);
      setEditingDomain(null);
      setDomainName("");
      fetchDomains();
    } catch (err) {
      console.error("Error saving domain:", err);
      toastError("Failed to save domain.");
    }
  };

  const handleVerifyDNS = async (domainId) => {
    try {
      setVerifying(true);
      const res = await api.post(`/api/domains/${domainId}/verify`);
      if (res.data.success) {
        toastSuccess(res.data.message);
        setShowModal(false);
        fetchDomains();
      } else {
        toastError(res.data.message || "DNS record not found. Please wait for propagation or check the record.");
        setActiveDomainForVerify(prev => prev ? { ...prev, dns_status: 'failed' } : null);
      }
    } catch (err) {
      toastError("Verification request failed.");
      setActiveDomainForVerify(prev => prev ? { ...prev, dns_status: 'failed' } : null);
    } finally {
      setVerifying(false);
    }
  };

  const handleManualScan = async (id, domainName) => {
    try {
      setScanningDomainId(id);
      toastSuccess(`Starting manual scan for ${domainName}...`);

      await api.post(`/api/domains/${id}/scan`);

      toastSuccess("Scan completed successfully!");
      fetchDomains(); // Refresh data
    } catch (err) {
      console.error("Manual scan error:", err);
      toastError("Failed to perform manual scan.");
    } finally {
      setScanningDomainId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this domain?")) return;

    try {
      await api.delete(`/api/domains/${id}`);
      toastSuccess("Domain deleted successfully");
      fetchDomains();
    } catch (err) {
      console.error("Delete error:", err);
      toastError("Failed to delete domain.");
    }
  };

  const openModal = (domain = null) => {
    setEditingDomain(domain);
    setDomainName(domain ? domain.domain_name : "");
    const needsVerification = domain && (domain.dns_status === 'pending' || domain.dns_status === 'failed');
    setModalStep(domain ? (needsVerification ? 2 : 1) : 1);
    setActiveDomainForVerify(domain);
    setShowModal(true);
  };

  const filteredDomains = useMemo(() => {
    return domains.filter((d) => {
      const matchesSearch = d.domain_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "All"
          ? true
          : statusFilter === "Online"
            ? d.domain_status === "1"
            : d.domain_status !== "1";
      return matchesSearch && matchesStatus;
    });
  }, [domains, searchTerm, statusFilter]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentDomains = useMemo(() => {
    return filteredDomains.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredDomains, indexOfFirstItem, indexOfLastItem]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const formatRelativeTime = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    if (diffInSeconds < 60) return "Just now";
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    return formatDateTime(date);
  };

  const getSSLExpiryInfo = (dateString) => {
    if (!dateString) return { content: "N/A" };
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    const formattedDate = formatDateOnly(dateString);
    let colorClass = 'text-black';
    if (diffDays < 0) colorClass = 'text-red-600';
    else if (diffDays <= 30) colorClass = 'text-[#d39e00]';

    return {
      content: (
        <>
          <div>{formattedDate}</div>
          <div className={`text-[0.85em] ${diffDays <= 30 ? 'font-bold' : 'font-normal'} ${colorClass}`}>
            {diffDays < 0 ? "Expired" : `${diffDays} days left`}
          </div>
        </>
      )
    };
  };

  return (
    <PageLayout title="My Scans">
      <style>{scansPageStyle}</style>
      <div className="scans-shell">
        <div className="scans-toolbar">
          <div className="row align-items-center g-3">
            <div className="col-md-4">
              <h5 className="scans-title d-flex align-items-center gap-2">
                My Scans
                <span className="scans-count">{domains.length}</span>
              </h5>
            </div>
            <div className="col-md-8">
              <div className="d-flex justify-content-md-end gap-2 flex-wrap">
                <select
                  className="form-select form-select-sm"
                  style={{ width: 140, borderRadius: 10 }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="All">All Status</option>
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                </select>
                <div className="input-group input-group-sm" style={{ maxWidth: 240 }}>
                  <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '10px 0 0 10px' }}>
                    <i className="las la-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0 ps-0"
                    style={{ borderRadius: '0 10px 10px 0' }}
                    placeholder="Search domains..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning"
                  style={{ borderRadius: 10, fontWeight: 600 }}
                  onClick={loadOpenFindings}
                  disabled={findingsLoading}
                >
                  <i className="las la-exclamation-circle me-1"></i>
                  Open issues
                </button>
                {discoveredCount > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-info position-relative"
                    style={{ borderRadius: 10, fontWeight: 600 }}
                    onClick={loadDiscoveredAssets}
                  >
                    <i className="las la-radar me-1"></i>
                    {discoveredCount} possible asset{discoveredCount !== 1 ? 's' : ''}
                  </button>
                )}
                <button className="btn btn-sm text-white" style={{ background: 'var(--s360-accent)', borderRadius: 10, fontWeight: 600 }} onClick={() => openModal()}>
                  <i className="las la-plus me-1"></i> Add domain
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="scans-table-wrap">
            <table className="table table-hover align-middle mb-0 scans-table">
              <thead>
                <tr>
                  <th className="ps-4" style={{ width: 48 }}>#</th>
                  <th>Domain</th>
                  <th className="text-center">Score</th>
                  <th className="text-center">SSL</th>
                  <th className="text-center">Expiry</th>
                  <th className="text-center">Grade</th>
                  <th className="text-center">Risk</th>
                  <th>DNS</th>
                  <th>Last scan</th>
                  <th className="text-center">Status</th>
                  <th className="text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : currentDomains.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center py-5 text-muted">
                      <i className="las la-inbox fa-3x mb-3 opacity-50"></i>
                      <p className="mb-0">No matching domains found</p>
                    </td>
                  </tr>
                ) : (
                  currentDomains.map((d, i) => {
                    const sslInfo = getSSLExpiryInfo(d.ssl_expiry);
                    const domainInfo = getSSLExpiryInfo(d.domain_expiry);
                    const needsVerify = d.dns_status !== 'connected';
                    return (
                      <tr key={d.id}>
                        <td className="ps-4 text-muted">{indexOfFirstItem + i + 1}</td>
                        <td>
                          <div className="scans-domain">{d.domain_name}</div>
                        </td>
                        <td className="text-center">
                          <ScoreBadge
                            score={d.traffic_data?.score}
                            grade={d.traffic_data?.grade}
                            riskLevel={d.traffic_data?.riskLevel}
                            breakdown={d.traffic_data?.scoreBreakdown}
                            domainId={d.id}
                            domainName={d.domain_name}
                            needsVerify={d.dns_status !== "connected"}
                            scanning={scanningDomainId === d.id}
                            onRunScan={handleManualScan}
                            onVerify={() => openModal(d)}
                          />
                        </td>
                        <td className="text-center small">{sslInfo.content}</td>
                        <td className="text-center small">{domainInfo.content}</td>
                        <td className="text-center">
                          {d.traffic_data?.grade ? (
                            <span className={`badge ${['A', 'B'].includes(d.traffic_data.grade) ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-10 text-warning'} border`}>
                              {d.traffic_data.grade}
                            </span>
                          ) : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center">
                          {d.traffic_data?.riskLevel ? (
                            <span className={`badge ${d.traffic_data.riskLevel === 'LOW' ? 'bg-success' : 'bg-danger'}`}>
                              {d.traffic_data.riskLevel}
                            </span>
                          ) : <span className="text-muted">—</span>}
                        </td>
                        <td>
                          {d.dns_status === 'connected' ? (
                            <div className="d-flex align-items-center">
                              <span className="dot-pulse dot-green" title="Ownership verified"></span>
                              <span className="small fw-semibold text-success">Connected</span>
                            </div>
                          ) : d.dns_status === 'failed' ? (
                            <div>
                              <div className="d-flex align-items-center mb-1">
                                <span className="dot-pulse dot-red"></span>
                                <span className="small fw-semibold text-danger">Halted</span>
                              </div>
                              <button className="btn btn-link btn-sm p-0 text-decoration-none small text-danger" onClick={() => openModal(d)}>Restore</button>
                            </div>
                          ) : (
                            <div>
                              <div className="d-flex align-items-center mb-1">
                                <span className="dot-pulse dot-yellow"></span>
                                <span className="small fw-semibold text-secondary">Pending</span>
                              </div>
                              <button className="btn btn-link btn-sm p-0 text-decoration-none small" onClick={() => openModal(d)}>Verify</button>
                            </div>
                          )}
                        </td>
                        <td><small className="text-muted">{formatRelativeTime(d.last_checked_at)}</small></td>
                        <td className="text-center">
                          <span className={`badge rounded-pill ${d.domain_status === "1" ? "bg-success" : "bg-secondary"}`}>
                            {d.domain_status === "1" ? "Online" : "Offline"}
                          </span>
                        </td>
                        <td className="text-end pe-4">
                          <div className="scans-actions">
                            <button
                              className="btn btn-outline-secondary"
                              disabled={scanningDomainId === d.id}
                              onClick={() => {
                                if (needsVerify) {
                                  toastError("Ownership verification required");
                                  openModal(d);
                                  return;
                                }
                                handleManualScan(d.id, d.domain_name);
                              }}
                              title={needsVerify ? "Verification required" : "Run deep scan"}
                            >
                              {scanningDomainId === d.id
                                ? <i className="las la-spinner la-spin"></i>
                                : <i className="las la-sync"></i>}
                            </button>
                            {needsVerify ? (
                              <button className="btn btn-outline-danger" onClick={() => openModal(d)}>
                                Verify
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                aria-haspopup="menu"
                                aria-expanded={toolsMenu?.domain?.id === d.id}
                                onClick={(e) => openToolsMenu(e, d)}
                              >
                                Tools <i className="las la-angle-down ms-1"></i>
                              </button>
                            )}
                            {!needsVerify && (
                              <button className="btn btn-outline-danger" onClick={() => handleDelete(d.id)} title="Delete">
                                <i className="las la-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <>
          <div className="modal-backdrop fade show z-[1040]" style={{ backdropFilter: 'blur(4px)', background: 'rgba(15, 23, 42, 0.45)' }}></div>
          <div className="modal show d-block z-[1050]" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
              <div className="modal-content connect-modal border-0">
                <div className="modal-header">
                  <div className="flex-grow-1">
                    <div className="connect-kicker mb-1">
                      Step {modalStep} of 3
                    </div>
                    <h5 className="modal-title mb-0">
                      {modalStep === 1 && (editingDomain ? "Edit domain" : "Add a domain")}
                      {modalStep === 2 && "Choose connection method"}
                      {modalStep === 3 && "Verify ownership"}
                    </h5>
                    {modalStep >= 2 && domainName && (
                      <span className="connect-domain-chip">
                        <i className="las la-globe-americas" style={{ color: 'var(--s360-accent)' }}></i>
                        {domainName}
                      </span>
                    )}
                  </div>
                  <button type="button" className="btn-close ms-2" onClick={() => setShowModal(false)} aria-label="Close"></button>
                </div>

                <div className="modal-body">
                  <div className="connect-wizard" aria-label="Setup progress">
                    {[
                      { n: 1, label: 'Domain' },
                      { n: 2, label: 'Connect' },
                      { n: 3, label: 'Verify' },
                    ].map(({ n, label }) => (
                      <div
                        key={n}
                        className={`connect-wizard-step ${modalStep === n ? 'is-active' : ''} ${modalStep > n ? 'is-done' : ''}`}
                      >
                        <div className="connect-wizard-dot">
                          {modalStep > n ? <i className="las la-check" style={{ fontSize: 13 }}></i> : n}
                        </div>
                        <div className="connect-wizard-label">{label}</div>
                      </div>
                    ))}
                  </div>

                  {modalStep === 1 && (
                    <form id="connect-step1-form" onSubmit={handleSave}>
                      <p className="connect-lead">
                        Enter the root domain you want Secure360 to monitor. We&apos;ll guide you through verification next.
                      </p>
                      <label className="form-label small fw-bold text-uppercase text-muted">Domain name</label>
                      <div className="input-group input-group-lg mb-0">
                        <span className="input-group-text bg-white border-end-0"><i className="las la-globe" style={{ color: 'var(--s360-accent)' }}></i></span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="example.com"
                          value={domainName}
                          onChange={(e) => setDomainName(e.target.value)}
                          required
                          style={{ borderRadius: '0 12px 12px 0' }}
                        />
                      </div>
                    </form>
                  )}

                  {modalStep === 2 && (
                    <div>
                      <p className="connect-lead mb-0">
                        Pick how you&apos;d like to prove ownership. Manual DNS works with any provider.
                      </p>

                      <div className="mt-3">
                        <button
                          type="button"
                          className={`connect-option ${connectionType === 'manual' ? 'is-selected' : ''}`}
                          onClick={() => setConnectionType('manual')}
                        >
                          <div className="d-flex align-items-start gap-3">
                            <span className="connect-radio">
                              {connectionType === 'manual' && <i className="las la-check" style={{ fontSize: 11 }}></i>}
                            </span>
                            <div className="flex-grow-1">
                              <p className="connect-option-title">
                                <i className="las la-dns connect-option-inline-icon"></i>
                                Manual DNS
                                <span className="connect-pill rec">Recommended</span>
                              </p>
                              <p className="connect-option-desc">
                                Add a TXT record at your DNS host. Takes about 2 minutes and works everywhere.
                              </p>
                            </div>
                          </div>
                        </button>

                        <button
                          type="button"
                          className="connect-option is-disabled"
                          disabled
                          title="Coming soon on Pro plans"
                        >
                          <div className="d-flex align-items-start gap-3">
                            <span className="connect-radio"></span>
                            <div className="flex-grow-1">
                              <p className="connect-option-title">
                                <i className="lab la-cloudflare connect-option-inline-icon"></i>
                                Cloudflare
                                <span className="connect-pill pro">Pro · Soon</span>
                              </p>
                              <p className="connect-option-desc mb-0">
                                One-click setup via API token — no manual DNS edits required.
                              </p>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {modalStep === 3 && (
                    <div>
                      {activeDomainForVerify?.dns_status === 'failed' ? (
                        <div className="alert alert-danger py-2 px-3 small mb-3 border-0" style={{ borderRadius: 12 }}>
                          <i className="las la-exclamation-circle me-1"></i>
                          TXT record not found yet. Confirm it&apos;s published, wait a few minutes, then try again.
                        </div>
                      ) : (
                        <p className="connect-lead mb-3">
                          Copy the record below into your DNS panel, then click verify.
                        </p>
                      )}

                      <div className="connect-record mb-2">
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <span className="badge bg-primary bg-opacity-10 text-primary px-2 py-1">TXT record</span>
                          <span className="small text-muted">TTL: Auto or 300s recommended</span>
                        </div>
                        <div className="row g-3 mb-3">
                          <div className="col-sm-4">
                            <label>Type</label>
                            <div className="fw-bold">TXT</div>
                          </div>
                          <div className="col-sm-8">
                            <label>Host / name</label>
                            <div className="fw-bold">@ <span className="fw-normal text-muted small">(root)</span></div>
                          </div>
                        </div>
                        <label>Value</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control font-monospace small bg-white"
                            value={`secure360-verification=${activeDomainForVerify?.verification_token || 'generating...'}`}
                            readOnly
                          />
                          <button
                            className="btn text-white"
                            style={{ background: 'var(--s360-accent)' }}
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(`secure360-verification=${activeDomainForVerify?.verification_token}`);
                              toastSuccess("Record value copied");
                            }}
                          >
                            <i className="las la-copy me-1"></i> Copy
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-footer d-flex flex-column flex-sm-row align-items-stretch">
                  {modalStep === 1 && (
                    <button type="submit" form="connect-step1-form" className="connect-cta w-100">
                      {editingDomain ? 'Save changes' : 'Continue'} <i className="las la-arrow-right ms-1"></i>
                    </button>
                  )}

                  {modalStep === 2 && (
                    <>
                      <button type="button" className="connect-back flex-sm-grow-1" onClick={() => setModalStep(1)}>
                        <i className="las la-arrow-left me-1"></i> Back
                      </button>
                      <button
                        type="button"
                        className="connect-cta flex-sm-grow-1"
                        onClick={() => setModalStep(3)}
                      >
                        Continue setup <i className="las la-arrow-right ms-1"></i>
                      </button>
                    </>
                  )}

                  {modalStep === 3 && (
                    <>
                      <button type="button" className="connect-back flex-sm-grow-1" onClick={() => setModalStep(2)}>
                        <i className="las la-arrow-left me-1"></i> Back
                      </button>
                      <button
                        type="button"
                        className="connect-cta flex-sm-grow-1"
                        style={activeDomainForVerify?.dns_status === 'failed' ? { background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)', boxShadow: '0 4px 12px rgba(220,38,38,0.25)' } : undefined}
                        onClick={() => handleVerifyDNS(activeDomainForVerify?.id)}
                        disabled={verifying}
                      >
                        {verifying
                          ? <><i className="las la-spinner la-spin me-1"></i> Checking DNS…</>
                          : <><i className="las la-shield-check me-1"></i> Verify connection</>}
                      </button>
                    </>
                  )}
                </div>

                {modalStep === 3 && (
                  <div className="text-center pb-3">
                    <button type="button" className="connect-secondary" onClick={() => setShowModal(false)}>
                      I&apos;ll verify later
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      <DomainScannerModal
        isOpen={!!scannerModalDomain}
        onClose={() => setScannerModalDomain(null)}
        domain={scannerModalDomain}
        onVerifySuccess={fetchDomains}
      />

      {toolsMenu && createPortal(
        <>
          <div
            className="tools-menu-backdrop"
            onPointerDown={(e) => {
              e.preventDefault();
              closeToolsMenu();
            }}
          />
          <div
            ref={toolsMenuRef}
            className="tools-menu"
            role="menu"
            aria-label={`Tools for ${toolsMenu.domain.domain_name}`}
            style={{
              top: toolsMenu.top,
              left: toolsMenu.left,
              width: toolsMenu.width,
              maxHeight: toolsMenu.maxHeight,
              overflowY: "auto",
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onWheel={(e) => {
              e.stopPropagation();
              const el = toolsMenuRef.current;
              if (!el) return;
              const canScroll = el.scrollHeight > el.clientHeight + 1;
              if (!canScroll) {
                e.preventDefault();
                return;
              }
              const atTop = el.scrollTop <= 0 && e.deltaY < 0;
              const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1 && e.deltaY > 0;
              if (atTop || atBottom) e.preventDefault();
            }}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div className="tools-menu-grid">
              <div className="tools-section">Monitoring</div>
              <button type="button" className="tools-item" role="menuitem" onClick={() => runToolsAction(() => navigate(`/dos/${toolsMenu.domain.domain_name}`))}>
                <i className="las la-tachometer-alt me-2"></i>DoS monitor
              </button>
              <button type="button" className="tools-item" role="menuitem" onClick={() => runToolsAction(() => navigate(`/security/${toolsMenu.domain.id}?name=${toolsMenu.domain.domain_name}`))}>
                <i className="las la-shield-alt me-2"></i>Security
              </button>
              <button type="button" className="tools-item tools-span" role="menuitem" onClick={() => runToolsAction(() => navigate(`/server-details/${toolsMenu.domain.domain_name}`))}>
                <i className="las la-server me-2"></i>Server details
              </button>

              <div className="tools-sep" />
              <div className="tools-section">Scans</div>
              <button type="button" className="tools-item" role="menuitem" onClick={() => runToolsAction(() => navigate(`/mscan-check/${toolsMenu.domain.domain_name}`))}>
                <i className="las la-bug me-2"></i>Malware
              </button>
              <button type="button" className="tools-item" role="menuitem" onClick={() => runToolsAction(() => navigate(`/scan-check/${toolsMenu.domain.domain_name}`))}>
                <i className="las la-network-wired me-2"></i>Ports
              </button>
              <button type="button" className="tools-item" role="menuitem" onClick={() => runToolsAction(() => navigate(`/build-details/${toolsMenu.domain.domain_name}`))}>
                <i className="las la-layer-group me-2"></i>Tech stack
              </button>
              <button type="button" className="tools-item" role="menuitem" onClick={() => runToolsAction(() => navigate(`/lighthouse-audit/${toolsMenu.domain.domain_name}?id=${toolsMenu.domain.id}`))}>
                <i className="las la-chart-bar me-2"></i>Lighthouse
              </button>
              <button type="button" className="tools-item tools-span" role="menuitem" onClick={() => runToolsAction(() => setScannerModalDomain(toolsMenu.domain))}>
                <i className="las la-fingerprint me-2"></i>Site crawler
              </button>

              <div className="tools-sep" />
              <button type="button" className="tools-item" role="menuitem" onClick={() => runToolsAction(() => navigate(`/ssl-check/${toolsMenu.domain.domain_name}`))}>
                <i className="las la-lock me-2"></i>SSL check
              </button>
              <button type="button" className="tools-item" role="menuitem" onClick={() => runToolsAction(() => openModal(toolsMenu.domain))}>
                <i className="las la-edit me-2"></i>Edit domain
              </button>
              <button type="button" className="tools-item is-danger tools-span" role="menuitem" onClick={() => runToolsAction(() => handleDelete(toolsMenu.domain.id))}>
                <i className="las la-trash me-2"></i>Delete
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {showDiscoveryModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(15,23,42,0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content" style={{ borderRadius: 14 }}>
              <div className="modal-header border-0">
                <h5 className="modal-title">Possible assets you may own</h5>
                <button type="button" className="btn-close" onClick={() => setShowDiscoveryModal(false)} />
              </div>
              <div className="modal-body pt-0">
                {discoveredAssets.length === 0 ? (
                  <p className="text-muted mb-0">No new discoveries right now.</p>
                ) : (
                  <div className="list-group list-group-flush">
                    {discoveredAssets.map((a) => (
                      <div key={a.id} className="list-group-item px-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div>
                          <div className="fw-semibold">{a.hostname}</div>
                          <small className="text-muted">via {a.discovery_source.replace('_', ' ')} · parent {a.parent_domain}</small>
                        </div>
                        <div className="btn-group btn-group-sm">
                          <button type="button" className="btn btn-success" onClick={() => handleClaimAsset(a.id)}>Claim</button>
                          <button type="button" className="btn btn-outline-secondary" onClick={() => handleDismissAsset(a.id)}>Dismiss</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showFindingsModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(15,23,42,0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content" style={{ borderRadius: 14 }}>
              <div className="modal-header border-0">
                <h5 className="modal-title">Open security issues</h5>
                <button type="button" className="btn-close" onClick={() => setShowFindingsModal(false)} />
              </div>
              <div className="modal-body pt-0">
                {openFindings.length === 0 ? (
                  <p className="text-muted mb-0">No open issues — you're in good shape.</p>
                ) : (
                  <div className="list-group list-group-flush">
                    {openFindings.map((f) => (
                      <div key={f.id} className="list-group-item px-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div>
                          <span className={`badge ${f.severity === 'critical' ? 'bg-danger' : f.severity === 'high' ? 'bg-warning text-dark' : 'bg-info text-dark'} me-2`}>{f.severity}</span>
                          <span className="fw-semibold">{f.title}</span>
                          <div className="small text-muted">{f.domain_name} · {f.source_module}</div>
                        </div>
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handleAcknowledgeFinding(f.id)}>Acknowledge</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default Scans;
